import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import { cashFlowInitialValues } from 'components/organisms/forms/cash-flow/CashFlowForm';
import { FormikHelpers } from 'formik';
import { useCustomParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiHandCoinsLight, PiPencilSimpleLine } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { IRepaymentPopupForm } from 'services/advance/types';
import {
  useDeleteCost,
  useGetCost,
  usePutRepaymentCost,
} from 'services/cash-flow/cost';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const Cost = () => {
  const labelId = useCustomParamId('id', '/label');
  const costId = useCustomParamId('cashFlowId', `/label/${labelId}/cost`);

  const { pathname } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const cost = useGetCost(costId);

  const removeCost = useDeleteCost();

  const goToListPath = useLocation().state === true ? 'unpaid-cost' : `label/${labelId}/balance`;

  const onGoToEdit = () =>
    navigate(`/label/${labelId}/cost/edit/${costId}/file`);

  const onRemoveCost = async (actions: PopupActions) => {
    await removeCost.mutateAsync(costId, {
      onSuccess: () => {
        actions.close();
        navigate('/' + goToListPath);
      },
    });
  };

  const repaymentCost = usePutRepaymentCost();

  const onRepaymentAdvance = async (
    values: IRepaymentPopupForm,
    formikHelpers: FormikHelpers<IRepaymentPopupForm>,
    actions: PopupActions | null
  ) => {
    await repaymentCost.mutateAsync(
      { costId: costId, ...values },
      {
        onSuccess: async () => {
          await cost.refetch();
          formikHelpers.resetForm();
          actions?.close();
        },
        onError: (error: any) => onErrorShowAllErrorsToast(error),
      }
    );
  };

  return (
    <Form.CashFlow
      initialValues={
        cost.data
          ? middleware.cost.queryToForm(cost.data)
          : cashFlowInitialValues
      }
      onSubmit={() => {}}
      tittle={cost.data?.costName}
      backPath={goToListPath}
      status={{
        isLoading: cost.isLoading,
        displayMode: true,
        refetch: cost.refetch,
      }}
      topbarContent={
        <div className='flex'>
          {!cost.data?.repayments.length && (
            <Button isClear onlyIcon onClick={onGoToEdit}>
              <Button.Description description={t('button.edit')} />

              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {!!cost.data?.remainingNetAmount && (
            <MagellanPopups.Repayment
              maxRepaymentValue={cost.data.remainingNetAmount}
              onSubmit={onRepaymentAdvance}
              name={cost.data?.costName}
              trigger={
                <Button
                  type='button'
                  isClear
                  variant={BUTTON_VARIANT.warning}
                  onlyIcon>
                  <Button.Description description={t('button.repay')} />

                  <Button.Icon icon={<PiHandCoinsLight />} />
                </Button>
              }
            />
          )}

          <MagellanPopups.DeleteWarning
            name={cost.data?.costName}
            onRemove={onRemoveCost}
          />
        </div>
      }
    />
  );
};

export default Cost;
